import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { FaVideo, FaRobot, FaClock, FaEye, FaChartLine, FaNewspaper } from 'react-icons/fa';

const TrendingCasesTab = () => {
  const [publicCasefiles, setPublicCasefiles] = useState([]);
  const [evidenceCounts, setEvidenceCounts] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // Fetch all public casefiles
  useEffect(() => {
    const q = query(
      collection(db, 'casefiles'),
      where('isPublic', '==', true),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const files = [];
      querySnapshot.forEach((doc) => {
        files.push({ id: doc.id, ...doc.data() });
      });
      setPublicCasefiles(files);
      setIsLoading(false);
    }, (error) => {
      console.error("Error fetching public casefiles:", error);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Fetch evidence counts for all public casefiles
  useEffect(() => {
    if (publicCasefiles.length === 0) return;

    // Firestore has a limit of 10 items in 'in' queries, so we need to handle this
    const casefileIds = publicCasefiles.map(cf => cf.id);
    // Temporary: Use simple collection query while indexes are building
    const q = query(collection(db, 'evidence'));

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const counts = {};
      
      // Initialize counts
      casefileIds.forEach(id => {
        counts[id] = {
          totalEvidence: 0,
          analyzedEvidence: 0,
          totalVehicles: 0,
          totalPlates: 0,
          recentActivity: 0
        };
      });

      // Process evidence
      querySnapshot.forEach((doc) => {
        const evidence = { id: doc.id, ...doc.data() };
        const casefileId = evidence.casefileId;
        
        // Only process evidence for public casefiles
        if (counts[casefileId]) {
          counts[casefileId].totalEvidence++;
          
          // Check for recent activity (last 7 days)
          const weekAgo = new Date();
          weekAgo.setDate(weekAgo.getDate() - 7);
          if (evidence.createdAt && evidence.createdAt.toDate() > weekAgo) {
            counts[casefileId].recentActivity++;
          }
          
          // Check if analyzed
          if (evidence.analysis && evidence.analysis.trim() !== '') {
            counts[casefileId].analyzedEvidence++;
            
            // Count vehicles and plates from AI analysis
            const analysisText = evidence.analysis;
            if (!analysisText.toLowerCase().includes('error:') && 
                analysisText.trim().toLowerCase() !== 'n/a') {
              
              try {
                const jsonMatch = analysisText.match(/\[.*\]|\{.*\}/s);
                if (jsonMatch) {
                  const jsonData = JSON.parse(jsonMatch[0]);
                  if (Array.isArray(jsonData)) {
                    counts[casefileId].totalVehicles += jsonData.length;
                    
                    const platesInEvidence = jsonData.filter(item => 
                      item["License Plate Number"] && 
                      item["License Plate Number"] !== 'Not detected' &&
                      item["License Plate Number"].length >= 2
                    ).length;
                    counts[casefileId].totalPlates += platesInEvidence;
                  }
                }
              } catch (e) {
                // Fallback parsing if needed
              }
            }
          }
        }
      });

      setEvidenceCounts(counts);
    });

    return () => unsubscribe();
  }, [publicCasefiles]);

  // Calculate trending score and sort cases
  const trendingCases = useMemo(() => {
    return publicCasefiles
      .map(casefile => {
        const counts = evidenceCounts[casefile.id] || {
          totalEvidence: 0,
          analyzedEvidence: 0,
          totalVehicles: 0,
          totalPlates: 0,
          recentActivity: 0
        };

        // Calculate trending score based on multiple factors
        const daysSinceCreation = casefile.createdAt 
          ? Math.floor((new Date() - casefile.createdAt.toDate()) / (1000 * 60 * 60 * 24))
          : 30;
        
        // Trending score formula: (evidence * 2) + (recent_activity * 3) + (plates * 5) + (time_decay)
        const timeDecay = Math.max(0, 10 - daysSinceCreation); // Newer cases get bonus
        const trendingScore = (counts.totalEvidence * 2) + 
                             (counts.recentActivity * 3) + 
                             (counts.totalPlates * 5) + 
                             timeDecay;

        return {
          ...casefile,
          counts,
          trendingScore
        };
      })
      .filter(casefile => casefile.counts.totalEvidence > 0) // Only show cases with evidence
      .sort((a, b) => b.trendingScore - a.trendingScore)
      .slice(0, 10); // Show top 10 trending cases
  }, [publicCasefiles, evidenceCounts]);

  const getTrendingBadge = (index) => {
    if (index === 0) return { text: '🔥 HOT', class: 'trending-hot' };
    if (index === 1) return { text: '⚡ TRENDING', class: 'trending-trending' };
    if (index === 2) return { text: '📈 POPULAR', class: 'trending-popular' };
    return null;
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString();
  };

  if (isLoading) {
    return (
      <div className="tab-content trending-cases-tab">
        <div className="tab-header">
          <h2>Trending Cases</h2>
          <p>Most popular and discussed cases</p>
        </div>
        <p>Loading trending cases...</p>
      </div>
    );
  }

  return (
    <div className="tab-content trending-cases-tab">
      <div className="tab-header">
        <h2>Trending Cases</h2>
        <p>Most popular cases based on evidence, activity, and engagement</p>
      </div>

      {trendingCases.length === 0 ? (
        <div className="empty-state">
          <FaNewspaper className="empty-icon" />
          <h3>No Trending Cases</h3>
          <p>No cases have enough evidence to be trending yet.</p>
          <p>Share evidence to help cases become popular!</p>
          <Link to="/dashboard" className="cta-button">
            <FaNewspaper /> Create Public Case
          </Link>
        </div>
      ) : (
        <div className="trending-cases-list">
          {trendingCases.map((casefile, index) => {
            const counts = casefile.counts;
            const trendingBadge = getTrendingBadge(index);

            return (
              <div key={casefile.id} className={`trending-case-card ${trendingBadge ? trendingBadge.class : ''}`}>
                {trendingBadge && (
                  <div className="trending-badge">
                    {trendingBadge.text}
                  </div>
                )}
                


                <div className="case-content">
                  <div className="case-header">
                    <Link to={`/casefile/${casefile.id}`} className="case-title">
                      {casefile.title}
                    </Link>
                    <div className="case-meta">
                      <span className="creator">
                        by {casefile.creatorEmail || 'Anonymous'}
                      </span>
                      <span className="time-ago">
                        <FaClock /> {formatTimeAgo(casefile.createdAt.toDate())}
                      </span>
                    </div>
                  </div>

                  <div className="case-description">
                    {casefile.description.length > 120 
                      ? `${casefile.description.substring(0, 120)}...` 
                      : casefile.description}
                  </div>

                  <div className="trending-stats">
                    <div className="stat-item">
                      <FaVideo className="stat-icon" />
                      <span className="stat-number">{counts.totalEvidence}</span>
                      <span className="stat-label">Evidence</span>
                    </div>
                    <div className="stat-item">
                      <FaRobot className="stat-icon" />
                      <span className="stat-number">{counts.analyzedEvidence}</span>
                      <span className="stat-label">Analyzed</span>
                    </div>
                    <div className="stat-item">
                      <FaChartLine className="stat-icon" />
                      <span className="stat-number">{counts.recentActivity}</span>
                      <span className="stat-label">This Week</span>
                    </div>
                    {counts.totalPlates > 0 && (
                      <div className="stat-item highlight">
                        <FaEye className="stat-icon" />
                        <span className="stat-number">{counts.totalPlates}</span>
                        <span className="stat-label">Plates Found</span>
                      </div>
                    )}
                  </div>



                  <div className="case-actions">
                    <Link to={`/casefile/${casefile.id}`} className="view-button">
                      <FaEye /> View Case
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="trending-footer">
        <p>Showing top {trendingCases.length} trending cases</p>
        <Link to="/public" className="view-all-button">
          View All Public Cases
        </Link>
      </div>
    </div>
  );
};

export default TrendingCasesTab; 